import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Toast, useT } from '@repo/common';

import AdminArticleCard from './AdminArticleCard';
import { GRID_CLASS, SkeletonGrid, StateMessage } from '../articleFeedStyles';
import {
  deleteArticle,
  getAdminArticles,
  hideArticle,
  publishArticle,
} from '../../../api/article/article.api';

/**
 * 어드민이 볼 때의 Article 피드.
 *
 * <p>공개 피드와 달리 초안·검토·숨김까지 전부 보여주고, 카드마다 상태 배지와
 * 케밥 메뉴(숨기기/삭제)가 붙는다. 본문 편집은 노션에서 하므로 Edit은 없다.
 */
const AdminArticleGrid = () => {
  const t = useT();

  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [toast, setToast] = useState(null);

  const fetchArticles = useCallback(() => {
    setIsLoading(true);
    setHasError(false);

    return getAdminArticles()
      .then(setArticles)
      .catch(() => setHasError(true))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const runAction = async (article, action, successMessage) => {
    setBusyId(article.id);
    try {
      await action(article.id);
      await fetchArticles();
      setToast({ type: 'success', title: successMessage });
    } catch {
      setToast({
        type: 'error',
        title: t(
          "Couldn't apply the change to Notion.",
          '노션에 반영하지 못했어요.',
        ),
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleHide = (article) =>
    runAction(article, hideArticle, t('Article hidden', '아티클을 숨겼어요'));

  /** Publish와 Set as Active는 같은 동작이지만 상태에 따라 안내 문구를 다르게 준다 */
  const handlePublish = (article) =>
    runAction(
      article,
      publishArticle,
      String(article.statusCode ?? '').toUpperCase() === 'HIDDEN'
        ? t('Article is now active', '아티클을 공개했어요')
        : t('Article published', '아티클을 발행했어요'),
    );

  const handleDeleteConfirm = async () => {
    const article = pendingDelete;
    setPendingDelete(null);
    if (!article) return;

    await runAction(
      article,
      deleteArticle,
      t('Article moved to Trash', '노션 휴지통으로 보냈어요'),
    );
  };

  const renderBody = () => {
    if (hasError) {
      return (
        <StateMessage
          title={t(
            "We couldn't load articles right now.",
            '지금은 아티클을 불러올 수 없어요.',
          )}
        />
      );
    }

    if (isLoading) return <SkeletonGrid />;

    if (articles.length === 0) {
      return (
        <StateMessage title={t('No articles yet.', '아직 아티클이 없어요.')} />
      );
    }

    return (
      <div className={GRID_CLASS}>
        {articles.map((article) => (
          <AdminArticleCard
            key={article.id}
            article={article}
            isBusy={busyId === article.id}
            onHide={handleHide}
            onPublish={handlePublish}
            onDelete={setPendingDelete}
          />
        ))}
      </div>
    );
  };

  return (
    <>
      {renderBody()}

      {pendingDelete && (
        <Modal
          isOpen
          title={t('Delete this article?', '이 아티클을 삭제할까요?')}
          subText={t(
            'It will be moved to the Notion trash. You can restore it there.',
            '노션 휴지통으로 이동합니다. 노션에서 복구할 수 있어요.',
          )}
          leftButtonLabel={t('Cancel', '취소')}
          rightButtonLabel={t('Delete', '삭제')}
          onClickLeftBtn={() => setPendingDelete(null)}
          onClickRightBtn={handleDeleteConfirm}
        />
      )}

      {toast && (
        <div className='fixed bottom-[30px] left-1/2 z-50 -translate-x-1/2'>
          <Toast
            type={toast.type}
            title={toast.title}
            hideActions
            onClose={() => setToast(null)}
          />
        </div>
      )}
    </>
  );
};

export default AdminArticleGrid;
